import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useNetworkContext } from "../network";
import { chatActions, chatSelectors } from "../chat";
import { authSelectors } from "../auth";
import { helpers } from "../../packages/helpers";
import { isChatOpen, isChatHide } from "./predicates";
import { useControlContext } from "./hook";

const { getChatSettings, resetUnreadCount, resetStatus } = chatActions;
const { selectChatId, selectChatSettings } = chatSelectors;
const { selectUserId } = authSelectors;
const { getStringValue } = helpers.text;
let intervalId: ReturnType<typeof setInterval> | null = null;

const clearPollingInterval = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

export const useSettingsLongPolling = () => {
  const dispatch = useAppDispatch();
  const { api, chatState } = useControlContext();
  const { isChatConnectError } = useNetworkContext();
  const { getChatSettingsApi } = api;

  const userId = useAppSelector(selectUserId);
  const chatId = useAppSelector(selectChatId);
  const { pollingTimeout } = useAppSelector(selectChatSettings);

  const params = {
    userId,
    chatId: getStringValue(chatId),
    getChatSettingsApi,
  };

  const getChatSettingsCallback = () => dispatch(getChatSettings(params));

  useEffect(() => {
    // первый запрос настроек чата, для корректного выставления таймаута
    if (chatId) {
      getChatSettingsCallback();
    }
  }, [chatId]);

  useEffect(() => {
    // запрос настроек при скрытии чата,
    // для получения корректного поллинг таймаута от backend
    if (isChatHide(chatState)) {
      dispatch(resetStatus("all"));
      getChatSettingsCallback();
    }
  }, [chatState]);

  useEffect(() => {
    const chatIsNotWorking = isChatConnectError || !userId || !chatId;

    if (chatIsNotWorking) {
      return;
    }

    const isOpen = isChatOpen(chatState);

    if (!isOpen && pollingTimeout) {
      clearPollingInterval();
      // устанавливает интервал на запросы о непрочитанных сообщениях
      intervalId = setInterval(getChatSettingsCallback, pollingTimeout);
    }

    // останавливает запросы когда чат открылся
    if (isOpen) {
      clearPollingInterval();
    }

    // сбрасывает счетчик непрочитанных сообщений когда чат открылся
    if (isOpen) {
      dispatch(resetUnreadCount());
    }
  }, [chatState, userId, chatId, pollingTimeout, isChatConnectError]);

  useEffect(
    () => () => {
      if (!intervalId) {
        return;
      }

      clearTimeout(intervalId);
      intervalId = null;
    },
    []
  );

  useEffect(() => {
    if (isChatConnectError && intervalId) {
      // останавливает запросы когда возникает ошибка сети
      clearTimeout(intervalId);
      intervalId = null;
    }
  }, [isChatConnectError]);
};
